import React from "react";
import moment from "moment";
import { ClipLoader } from "react-spinners";

import { fmtBRL, fmtInt } from "./formatters";
import { rotuloDocumento, rotuloDocumentos } from "../../../../utils/modeloDocumento";

const TARGET_TITULOS = {
  "previsto-nf": "Previsto — Notas Fiscais",
  "realizado-cte": "Realizado — CTes",
  "realizado-nf": "Realizado — Notas Fiscais",
  arealizar: "A realizar — Notas Fiscais",
  "realizado-fatura": "Realizado em fatura — Faturas",
  "realizado-fatura-cte": "Realizado em fatura — CTes",
  "arealizar-fatura": "A realizar fatura — CTes",
  "fatura-recebida": "Faturas recebidas",
  "fatura-cancelada": "Faturas canceladas",
  "fatura-andamento": "Faturas em andamento",
  "fatura-liberada-paga": "Faturas liberadas / integradas / pagas",
};

const fmtDate = (v) => (v ? moment(v).format("DD/MM/YYYY") : "-");

// Documentos de transporte (CT-e e NFse, PO1-3299): o plural segue o modelo
// das linhas; lista mista vira "documento(s)".
const getTipoLabel = (tipo, itens) => {
  if (tipo === "nfse") return "NFse(s)";
  if (tipo === "cte") return rotuloDocumentos(itens);
  if (tipo === "fatura") return "fatura(s)";
  return "nota(s) fiscal(is)";
};

const getTargetTitle = (target) => TARGET_TITULOS[target] || "Documentos";

const REGIAO_POR_UF = {
  AC: "Norte",
  AL: "Nordeste",
  AP: "Norte",
  AM: "Norte",
  BA: "Nordeste",
  CE: "Nordeste",
  DF: "Centro-Oeste",
  ES: "Sudeste",
  GO: "Centro-Oeste",
  MA: "Nordeste",
  MT: "Centro-Oeste",
  MS: "Centro-Oeste",
  MG: "Sudeste",
  PA: "Norte",
  PB: "Nordeste",
  PR: "Sul",
  PE: "Nordeste",
  PI: "Nordeste",
  RJ: "Sudeste",
  RN: "Nordeste",
  RS: "Sul",
  RO: "Norte",
  RR: "Norte",
  SC: "Sul",
  SP: "Sudeste",
  SE: "Nordeste",
  TO: "Norte",
};

const FATURA_STATUS = {
  AVA: {
    label: "A Validar",
    tone: "warning",
    description: "Fatura recebida da transportadora, aguardando o início da validação.",
  },
  EVA: {
    label: "Em Validação",
    tone: "info",
    description: "Fatura em processo de validação dos CT-es cobrados.",
  },
  EAN: {
    label: "Em Análise",
    tone: "warning",
    description:
      "A conferência terminou com pendência; aguardando a decisão do auditor (aprovar, devolver ou enviar para tratativa).",
  },
  ETR: {
    label: "Em Tratativa",
    tone: "info",
    description:
      "Fatura com pendência encaminhada a um responsável para resolver e revalidar.",
  },
  CAN: {
    label: "Cancelada",
    tone: "danger",
    description: "Fatura cancelada, fora do fluxo de cobrança.",
  },
  BLO: {
    label: "Bloqueada",
    tone: "danger",
    description: "Fatura bloqueada para pagamento até a resolução de pendências.",
  },
  CON: {
    label: "Contestada",
    tone: "warning",
    description: "Fatura contestada por divergência encontrada na validação.",
  },
  VAL: {
    label: "Validada",
    tone: "success",
    description: "Fatura validada e aprovada na auditoria.",
  },
  LIB: {
    label: "Liberada",
    tone: "info",
    description: "Fatura liberada para pagamento.",
  },
  PAG: {
    label: "Integrada",
    tone: "success",
    description: "Fatura integrada ao financeiro; aguarda a confirmação do pagamento.",
  },
  PGO: {
    label: "Paga",
    tone: "success",
    description: "Pagamento da fatura confirmado, fluxo encerrado.",
  },
};

const CTE_FATURAMENTO_STATUS = {
  AFT: {
    label: "A Faturar",
    tone: "warning",
    description: "O CT-e ainda não foi faturado.",
  },
  EFT: {
    label: "Em Faturamento",
    tone: "info",
    description: "O CT-e está em processo de faturamento.",
  },
  FTD: {
    label: "Faturado",
    tone: "success",
    description: "O CT-e foi faturado e o fluxo de faturamento foi encerrado.",
  },
  ANU: {
    label: "Anulado",
    tone: "danger",
    description: "O CT-e foi anulado e o fluxo foi encerrado.",
  },
};

const NF_STATUS = {
  ACT: {
    label: "Aguardando faturamento",
    tone: "warning",
    description: "NF aguardando CT-e para iniciar o fluxo de faturamento.",
  },
  FAT: {
    label: "Faturada",
    tone: "success",
    description: "Frete da NF faturado.",
  },
  FTD: {
    label: "Faturada",
    tone: "success",
    description: "Frete da NF faturado.",
  },
  FTA: {
    label: "Faturada",
    tone: "success",
    description: "Frete da NF faturado.",
  },
  LIB: {
    label: "Liberada",
    tone: "info",
    description: "Frete da NF liberado para pagamento.",
  },
  PAG: {
    label: "Paga",
    tone: "success",
    description: "Frete da NF pago, fluxo encerrado.",
  },
  CAN: {
    label: "Cancelada",
    tone: "danger",
    description: "NF cancelada, fora do fluxo de faturamento.",
  },
  BLO: {
    label: "Bloqueada",
    tone: "danger",
    description: "Frete da NF bloqueado para pagamento até a resolução de pendências.",
  },
  CON: {
    label: "Contestada",
    tone: "warning",
    description: "Frete da NF contestado por divergência encontrada na validação.",
  },
  VAL: {
    label: "Validada",
    tone: "success",
    description: "Frete da NF validado na auditoria.",
  },
};

const makeStatusCell = ({ code, label, tone, description }) => ({
  kind: "status",
  code,
  label: label || code || "Sem situação",
  tone: tone || "neutral",
  description,
});

const makeCteFaturamentoStatusCell = (row) => {
  const status = CTE_FATURAMENTO_STATUS[row.situacaoCteFaturaCodigo] || {};
  return makeStatusCell({
    code: row.situacaoCteFaturaCodigo,
    label: status.label || row.situacaoCteFaturaNome || row.situacaoCteFaturaCodigo,
    tone: status.tone,
    description: status.description,
  });
};

const makeFaturaStatusCell = (code) => {
  const status = FATURA_STATUS[code] || {};
  return makeStatusCell({
    code,
    label: status.label || code,
    tone: status.tone,
    description: status.description,
  });
};

const makeNfStatusCell = (row) =>
  {
    const status = NF_STATUS[row.situacaoNfCodigo] || {};
    return makeStatusCell({
      code: row.situacaoNfCodigo,
      label: status.label || row.situacaoNfDescricao || row.situacaoNfCodigo,
      tone: status.tone,
      description: status.description,
    });
  };

const makeRegionCell = (name) => ({
  kind: "region",
  label: name || "Sem região",
  empty: !name,
});

const getRegiaoPorUf = (uf) =>
  REGIAO_POR_UF[String(uf || "").toUpperCase()] || null;

const getExportValue = (value) => {
  if (value && typeof value === "object") return value.label || "";
  return value;
};

const getColumnClassName = (column, numericIndexes, colIndex) => {
  if (column === "Qtd documentos" || column === "Qtd NF") return "num-center";
  return numericIndexes.includes(colIndex) ? "num" : "text-left";
};

const getDocumentRows = (data) => {
  if (!data) return { columns: [], rows: [] };

  if (data.tipo === "cte") {
    return {
      columns: [
        "Documento",
        "Chave",
        "Emissão",
        "Transportadora",
        "Situação",
        "Qtd NF",
        "Valor prest.",
      ],
      numericIndexes: [5, 6],
      rows: data.itens.map((r) => [
        r.numero ? `${rotuloDocumento(r.modelo)} ${r.numero}` : "-",
        r.chave || "-",
        fmtDate(r.dataEmissao),
        r.transportadoraFantasia || "-",
        makeCteFaturamentoStatusCell(r),
        fmtInt(r.nfsCount),
        fmtBRL(r.valor),
      ]),
    };
  }

  if (data.tipo === "fatura") {
    return {
      columns: ["Fatura", "Situação", "Vencimento", "Valor total frete"],
      numericIndexes: [3],
      rows: data.itens.map((r) => [
        r.numero || `#${r.faturaId}`,
        makeFaturaStatusCell(r.situacaoFaturaCodigo),
        fmtDate(r.vencimento),
        fmtBRL(r.valTotalFrete),
      ]),
    };
  }

  return {
    columns: [
      "NF",
      "Série",
      "Emissão",
      "Transportadora",
      "Negócio",
      "UF",
      "Região",
      "Situação",
      "Qtd documentos",
      "Frete sim.",
      "Frete real.",
      "Valor NF",
    ],
    numericIndexes: [8, 9, 10, 11],
    rows: data.itens.map((r) => [
      r.nfNumero || "-",
      r.nfSerie || "-",
      fmtDate(r.nfDataEmissao),
      r.transportadoraFantasia || "-",
      r.negocioNome || "-",
      r.estadoSigla || "-",
      makeRegionCell(getRegiaoPorUf(r.estadoSigla)),
      makeNfStatusCell(r),
      fmtInt(r.qtdCtes),
      fmtBRL(r.valFreteSimulado + r.valFreteAdicional),
      fmtBRL(r.valFreteRealizado + r.valFreteRealizadoAdicional),
      fmtBRL(r.nfValorTotal),
    ]),
  };
};

const escapeHtml = (value) =>
  String(value === null || value === undefined ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export function exportFaturamentoDocuments(data, context) {
  const { columns, rows } = getDocumentRows(data);
  if (!rows.length) return;

  const title = context && context.label ? context.label : getTargetTitle(context && context.target);
  const tableRows = [
    `<tr>${columns.map((col) => `<th>${escapeHtml(col)}</th>`).join("")}</tr>`,
    ...rows.map(
      (row) =>
        `<tr>${row
          .map(
            (cell) =>
              `<td style="mso-number-format:'\\@';">${escapeHtml(
                getExportValue(cell)
              )}</td>`
          )
          .join("")}</tr>`
    ),
  ].join("");

  const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
  </head>
  <body>
    <h3>${escapeHtml(title)}</h3>
    <table border="1">${tableRows}</table>
  </body>
</html>`;

  const blob = new Blob(["\uFEFF", html], {
    type: "application/vnd.ms-excel;charset=utf-8;",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `faturamento-documentos-${moment().format("YYYY-MM-DD-HHmm")}.xls`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function DocumentCell({ value }) {
  if (value && value.kind === "status") {
    return (
      <span
        className={`faturamento-status-pill faturamento-status-pill--${value.tone}`}
        title={
          value.description ||
          (value.code ? `Código: ${value.code}` : undefined)
        }
      >
        {value.label}
      </span>
    );
  }

  if (value && value.kind === "region") {
    return (
      <span
        className={
          value.empty
            ? "faturamento-region-label faturamento-region-label--empty"
            : "faturamento-region-label"
        }
      >
        {value.label}
      </span>
    );
  }

  return value;
}

function FaturamentoDocumentTable({ data }) {
  const { columns, numericIndexes, rows } = getDocumentRows(data);

  return (
    <div className="faturamento-wrapper faturamento-document-wrapper">
      <table className="faturamento-table faturamento-document-table">
        <thead>
          <tr>
            {columns.map((column, colIndex) => (
              <th
                key={column}
                className={getColumnClassName(column, numericIndexes, colIndex)}
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={`${data.tipo}-${rowIndex}`}>
              {row.map((cell, colIndex) => (
                <td
                  key={`${data.tipo}-${rowIndex}-${colIndex}`}
                  className={getColumnClassName(
                    columns[colIndex],
                    numericIndexes,
                    colIndex
                  )}
                >
                  <DocumentCell value={cell} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FaturamentoDocumentList({ loading, error, data, context }) {
  const count = data && data.itens ? data.itens.length : 0;

  if (loading) {
    return (
      <div className="faturamento-state-panel">
        <ClipLoader size={42} color="#1d8cf8" loading />
        <h4>Carregando documentos</h4>
        <p>Buscando os documentos conforme a seleção da visão superior.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="faturamento-state-panel faturamento-state-panel--danger">
        <div className="faturamento-state-panel__eyebrow">Erro</div>
        <h4>Não foi possível carregar os documentos</h4>
        <p>{typeof error === "object" ? JSON.stringify(error) : error}</p>
      </div>
    );
  }

  if (!context) {
    return (
      <div className="faturamento-state-panel">
        <div className="faturamento-state-panel__eyebrow">Documentos</div>
        <h4>Selecione um recorte na visão superior</h4>
        <p>Use o mapa, ranking, esteira ou heatmap para alimentar esta lista.</p>
      </div>
    );
  }

  if (!data || count === 0) {
    return (
      <div className="faturamento-state-panel">
        <div className="faturamento-state-panel__eyebrow">
          {getTargetTitle(context.target)}
        </div>
        <h4>Nenhum documento encontrado</h4>
        <p>{context.label}</p>
      </div>
    );
  }

  return (
    <>
      <div className="faturamento-document-summary">
        <div>
          <span>{getTargetTitle(context.target)}</span>
          <strong>
            {fmtInt(count)} {getTipoLabel(data.tipo, data.itens)}
          </strong>
        </div>
        {context.label && <p>{context.label}</p>}
      </div>
      <FaturamentoDocumentTable data={data} />
    </>
  );
}

export default React.memo(FaturamentoDocumentList);
